// src/modules/admin/pages/AdminUsers/index.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Form, Table, Badge, Button, OverlayTrigger, Tooltip, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../../shared/hooks/useAuth';
import { useService } from '../../../../shared/hooks/useService';
import { useWarmupTimer } from '../../../../shared/hooks/useWarmupTimer';
import Loading from '../../../../shared/components/Loading';
import ErrorState from '../../../../shared/components/ErrorState';
import UserEditModal from '../../components/UserEditModal';
import { ALL_PERMISSIONS } from '../../../../shared/constants/permissions';
import './styles.css';

const PAGE_SIZE = 20;

const AdminUsers = () => {
    const [users, setUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({
        hasPermissions: false,
    });
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(false);
    const [error, setError] = useState(null);
    const [editingUser, setEditingUser] = useState(null);
    const { isWarmup } = useWarmupTimer(isLoading);
    const service = useService();
    const { hasPermission } = useAuth();
    const hasUsersPermission = hasPermission('users:managePermissions');

    const loadMoreRef = useRef(null);

    // Refs for stable access inside callbacks
    const nextTokenRef = useRef(null);
    const isLoadingMoreRef = useRef(false);
    const abortRef = useRef(0);
    const observerRef = useRef(null);

    const fetchPage = useCallback(async (append, token, abortId, currentFilters) => {
        try {
            const result = await service.getAllUsers({
                pageSize: PAGE_SIZE,
                nextToken: token,
                filters: currentFilters,
            });

            if (abortRef.current !== abortId) return;

            if (append) {
                setUsers(prev => [...prev, ...result.users]);
            } else {
                setUsers(result.users);
            }

            nextTokenRef.current = result.nextToken || null;
            setHasMore(!!result.nextToken);
        } catch (err) {
            if (abortRef.current !== abortId) return;
            setError(err.message);
        } finally {
            if (abortRef.current !== abortId) return;
            setIsLoading(false);
            isLoadingMoreRef.current = false;
            setIsLoadingMore(false);
        }
    }, [service]);

    // Reset and load first page whenever filters change
    useEffect(() => {
        const abortId = ++abortRef.current;
        nextTokenRef.current = null;
        isLoadingMoreRef.current = false;
        setIsLoadingMore(false);
        setHasMore(false);
        setIsLoading(true);
        setError(null);
        fetchPage(false, null, abortId, filters);
    }, [filters, fetchPage]);

    // IntersectionObserver for infinite scroll
    useEffect(() => {
        if (isLoading) return;

        const observer = new IntersectionObserver(
            entries => {
                if (
                    entries[0].isIntersecting &&
                    nextTokenRef.current &&
                    !isLoadingMoreRef.current
                ) {
                    const abortId = abortRef.current;
                    isLoadingMoreRef.current = true;
                    setIsLoadingMore(true);
                    fetchPage(true, nextTokenRef.current, abortId, filters);
                }
            },
            { rootMargin: '200px' }
        );

        observerRef.current = observer;

        const currentRef = loadMoreRef.current;
        if (currentRef) observer.observe(currentRef);

        return () => observer.disconnect();
    }, [isLoading, hasMore, fetchPage, filters]);

    // Re-observe sentinel after users update so observer re-fires if still visible
    useEffect(() => {
        if (isLoading) return;
        requestAnimationFrame(() => {
            if (observerRef.current && loadMoreRef.current) {
                observerRef.current.disconnect();
                observerRef.current.observe(loadMoreRef.current);
            }
        });
    }, [users, isLoading]);

    // Client-side search on loaded users
    const [filteredUsers, setFilteredUsers] = useState([]);
    useEffect(() => {
        let result = [...users];

        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase().trim();
            result = result.filter(u => {
                const fullName = `${u.firstName || ''} ${u.lastName || ''}`.toLowerCase();
                const username = (u.username || '').toLowerCase();
                return fullName.includes(term) || username.includes(term);
            });
        }

        setFilteredUsers(result);
    }, [searchTerm, users]);

    const toggleFilter = (key) => {
        setFilters(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const handleSave = async (username, updates) => {
        try {
            await service.updateProfile(username, updates);
            if ('profilePicture' in updates) {
                // Refetch to get fresh presigned URLs for profile pictures
                const abortId = ++abortRef.current;
                nextTokenRef.current = null;
                setIsLoading(true);
                setHasMore(false);
                setError(null);
                await fetchPage(false, null, abortId, filters);
            } else {
                setEditingUser(prev => (prev && prev.username === username ? { ...prev, ...updates } : prev));
                setUsers(prev => prev.map(u => (u.username === username ? { ...u, ...updates } : u)));
            }
        } catch (err) {
            throw err;
        }
    };

    // Compact permissions display
    const renderPermissions = (permissions) => {
        if (!permissions || permissions.length === 0) {
            return <span className="text-muted">—</span>;
        }

        if (permissions.length <= 2) {
            return (
                <div className="d-flex flex-wrap gap-1">
                    {permissions.map(key => {
                        const perm = ALL_PERMISSIONS.find(p => p.key === key);
                        if (!perm) return null;
                        return (
                            <OverlayTrigger
                                key={key}
                                placement="top"
                                overlay={<Tooltip>{perm.label}</Tooltip>}
                            >
                                <Badge bg="secondary" className="d-flex align-items-center gap-1 py-1 px-2 user-perm-badge">
                                    <i className={`bi ${perm.icon}`} style={{ fontSize: '0.75rem' }}></i>
                                    {perm.label}
                                </Badge>
                            </OverlayTrigger>
                        );
                    })}
                </div>
            );
        }

        // 3+ permissions: show first 2 + "+N more"
        return (
            <div className="d-flex flex-wrap gap-1">
                {permissions.slice(0, 2).map(key => {
                    const perm = ALL_PERMISSIONS.find(p => p.key === key);
                    if (!perm) return null;
                    return (
                        <OverlayTrigger
                            key={key}
                            placement="top"
                            overlay={<Tooltip>{perm.label}</Tooltip>}
                        >
                            <Badge bg="secondary" className="d-flex align-items-center gap-1 py-1 px-2 user-perm-badge">
                                <i className={`bi ${perm.icon}`} style={{ fontSize: '0.75rem' }}></i>
                                {perm.label}
                            </Badge>
                        </OverlayTrigger>
                    );
                })}
                <Badge bg="secondary" className="py-1 px-2">
                    +{permissions.length - 2} more
                </Badge>
            </div>
        );
    };

    // Get user full name and initials
    const getFullName = (user) => {
        return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username;
    };

    const getInitials = (user) => {
        const first = (user.firstName || '?')[0];
        const last = (user.lastName || '?')[0];
        return `${first}${last}`.toUpperCase();
    };

    const handleEdit = (user) => {
        setEditingUser(user);
    };

    if (isLoading) return <Loading isWarmup={isWarmup} />;
    if (!hasUsersPermission) {
        return (
            <Container className="admin-users-container">
                <Alert variant="warning" className="admin-no-permission-alert">
                    <Alert.Heading>
                        <i className="bi bi-exclamation-triangle me-2"></i>
                        No Permission
                    </Alert.Heading>
                    <p>You need the <strong>users:managePermissions</strong> permission to view and manage user permissions.</p>
                    <Button variant="outline-warning" size="sm" as={Link} to="/admin">
                        <i className="bi bi-arrow-left me-1"></i>
                        Back to Dashboard
                    </Button>
                </Alert>
            </Container>
        );
    }
    if (error) return <ErrorState message={error} onRetry={() => setFilters(f => ({ ...f }))} />;

    return (
        <div className="admin-users-page">
            <Container>
                <div className="admin-users-header">
                    <h1>Users</h1>
                    <p className="header-subtitle">
                        Manage and search all users
                    </p>
                </div>

                <div className="controls-bar">
                    <Form.Control
                        type="text"
                        placeholder="Search by name or username..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                    />
                </div>

                <div className="filters-bar">
                    <button
                        className={`filter-chip ${filters.hasPermissions ? 'active' : ''}`}
                        onClick={() => toggleFilter('hasPermissions')}
                    >
                        <i className="bi bi-shield-lock me-1"></i>
                        Has Permissions
                    </button>
                </div>

                {filteredUsers.length > 0 ? (
                    <>
                        {/* Desktop table view */}
                        <div className="table-wrapper d-none d-md-block">
                            <Table hover className="users-table">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Username</th>
                                        <th>Email</th>
                                        <th>Permissions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredUsers.map(user => (
                                        <tr key={user.username} onClick={() => handleEdit(user)} className="user-row-clickable">
                                            <td>
                                                <div className="user-cell-name">
                                                    {user.profilePicture ? (
                                                        <img
                                                            src={user.profilePicture}
                                                            alt={getFullName(user)}
                                                            className="user-avatar"
                                                        />
                                                    ) : (
                                                        <div className="user-avatar-placeholder">
                                                            {getInitials(user)}
                                                        </div>
                                                    )}
                                                    <span className="user-name">{getFullName(user)}</span>
                                                </div>
                                            </td>
                                            <td className="user-cell-username">{user.username}</td>
                                            <td className="user-cell-email">{user.email}</td>
                                            <td>{renderPermissions(user.permissions)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>

                        {/* Mobile card view */}
                        <div className="d-md-none users-card-list">
                            {filteredUsers.map(user => (
                                <div key={user.username} className="user-card user-row-clickable" onClick={() => handleEdit(user)}>
                                    <div className="user-card-header">
                                        {user.profilePicture ? (
                                            <img
                                                src={user.profilePicture}
                                                alt={getFullName(user)}
                                                className="user-card-avatar"
                                            />
                                        ) : (
                                            <div className="user-card-avatar-placeholder">
                                                {getInitials(user)}
                                            </div>
                                        )}
                                        <div className="user-card-name-section">
                                            <div className="user-card-name">{getFullName(user)}</div>
                                            <div className="user-card-username">@{user.username}</div>
                                        </div>
                                    </div>
                                    <div className="user-card-body">
                                        <div className="user-card-row">
                                            <span className="user-card-label">Email</span>
                                            <span className="user-card-value">{user.email}</span>
                                        </div>
                                        <div className="user-card-row">
                                            <span className="user-card-label">Permissions</span>
                                            <span className="user-card-value">
                                                {renderPermissions(user.permissions)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                ) : (
                    <div className="empty-state">
                        <div className="empty-state-icon">👥</div>
                        <h3>No users found</h3>
                        <p>
                            {searchTerm
                                ? 'Try a different search term.'
                                : 'No users match your filters.'}
                        </p>
                        {searchTerm && (
                            <button className="clear-search-btn" onClick={() => setSearchTerm('')}>
                                Clear search
                            </button>
                        )}
                    </div>
                )}

                <div className="load-more-container" ref={loadMoreRef}>
                    {isLoadingMore && (
                        <div className="loading-indicator">
                            Loading more users...
                        </div>
                    )}
                </div>
            </Container>

            <UserEditModal
                show={!!editingUser}
                user={editingUser}
                onSave={handleSave}
                onClose={() => setEditingUser(null)}
            />
        </div>
    );
};

export default AdminUsers;
